import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ArrowRight, History, Search, X } from 'lucide-react';
import { useDebounce } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get } from '../../nucleo/api';
import { modulosWeb, type FonteBuscaGlobal, type ItemBuscaGlobal } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';

/**
 * CRM no novo desenho, lote 6 (N8, ficha `demandas/entrada/2026-10-26_malgo_MOD_crm-novo-desenho.md`): a busca da barra superior,
 * como no clone do RD. Vazia, mostra as buscas recentes (no navegador, por pessoa); com 2 letras ou mais, busca nos grupos que
 * os módulos publicam (`buscaGlobal`: negociações no Pipeline, empresas e contatos no Cadastros), só os que a pessoa pode ver.
 * Teclado de combobox: setas, Enter abre, Esc limpa e fecha. No celular, a lupa abre o campo numa faixa sob a barra.
 *
 * Lista de 30/10 (item 10, busca em todo o sistema): uma requisição só por termo (`GET /busca-global`, que roda no servidor a
 * listagem de cada módulo com a busca e o limite, com permissão, página e empresa conferidas lá também). O termo espera 250 ms
 * parado e a requisição do termo anterior é cancelada (o `signal` do React Query). Cada grupo traz o ícone, até 5 itens e,
 * quando há mais e a página aceita `?busca=`, a opção "Ver todos" (também pelo teclado). O sub-sistema não filtra a busca.
 *
 * Lista de 30/10 B (item 5): depois de buscar, o campo fica limpo: ao abrir um resultado, no Esc, no X, no clique fora e no Tab
 * para fora da caixa.
 */
const MAX_RECENTES = 5;
const chaveRecentes = (usuario: string) => `malgoos.busca_recentes.${usuario}`;

function lerRecentes(usuario: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(chaveRecentes(usuario)) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, MAX_RECENTES) : [];
  } catch {
    return [];
  }
}
function guardarRecente(usuario: string, termo: string) {
  try {
    const lista = [termo, ...lerRecentes(usuario).filter((x) => x.toLowerCase() !== termo.toLowerCase())].slice(0, MAX_RECENTES);
    localStorage.setItem(chaveRecentes(usuario), JSON.stringify(lista));
  } catch {
    // sem armazenamento (aba privada): a busca funciona, só não lembra
  }
}

interface Grupo {
  fonte: FonteBuscaGlobal & { slug: string };
  itens: ItemBuscaGlobal[];
  /** "Ver todos": a página da lista com o termo (só quando há mais itens e a página lê `?busca=`). */
  todos: string | null;
}

export interface RespostaBuscaGlobal {
  termo: string;
  grupos: { modulo: string; chave: string; estado: 'ok' | 'indisponivel'; itens: unknown[]; mais: boolean }[];
}

/**
 * As fontes da busca que a pessoa pode usar (módulo instalado, a permissão do grupo e, review rval1-1 VIS-M2, a página do
 * grupo liberada: com Contatos e Empresas negados no papel, a busca não lista contatos nem empresas; rl30-1 SEG-M2: o grupo de
 * várias páginas sai com todas negadas).
 */
export function fontesDaBusca(s: { instalado(slug: string): boolean; pode(permissao: string): boolean; paginaLiberada?(slug: string, aba: string): boolean }, lista = modulosWeb) {
  const liberada = (p?: string) => !p || (s.paginaLiberada?.(p.slice(0, p.indexOf('.')), p.slice(p.indexOf('.') + 1)) ?? true);
  return lista
    .flatMap((m) => (s.instalado(m.slug) ? (m.buscaGlobal ?? []).filter((f) => s.pode(f.permissao) && liberada(f.pagina) && (!f.paginas?.length || f.paginas.some(liberada))).map((f) => ({ ...f, slug: m.slug })) : []))
    .sort((a, b) => a.posicao - b.posicao);
}

/**
 * Junta a resposta do servidor às fontes da tela, na ordem delas: grupo que o servidor não mandou, indisponível ou vazio sai;
 * linha que a fonte não consegue montar (formato inesperado) sai sozinha. Exportada para os testes.
 */
export function gruposDaResposta(fontes: (FonteBuscaGlobal & { slug: string })[], r: RespostaBuscaGlobal, tDe: (slug: string) => TFunction, fuso?: string): { grupos: Grupo[]; indisponiveis: number } {
  const porChave = new Map(r.grupos.map((g) => [`${g.modulo}.${g.chave}`, g]));
  const grupos: Grupo[] = [];
  let indisponiveis = 0;
  for (const fonte of fontes) {
    const g = porChave.get(`${fonte.slug}.${fonte.chave}`);
    if (!g) continue;
    if (g.estado !== 'ok') {
      indisponiveis += 1;
      continue;
    }
    const t = tDe(fonte.slug);
    const itens = g.itens.flatMap((linha) => {
      try {
        return [fonte.item(linha, t, fuso)];
      } catch {
        return [];
      }
    });
    if (itens.length) grupos.push({ fonte, itens, todos: g.mais ? (fonte.verTodos?.(r.termo) ?? null) : null });
  }
  return { grupos, indisponiveis };
}

export function BuscaGlobal() {
  const { t, i18n } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const id = useId();
  const [termo, setTermo] = useState('');
  const [aberta, setAberta] = useState(false);
  const [celular, setCelular] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const [recentes, setRecentes] = useState(() => lerRecentes(s.usuario.id));
  const caixa = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const fontes = useMemo(() => fontesDaBusca(s), [s]);
  const busca = useDebounce(termo.trim(), 250);
  const pronta = busca.length >= 2;
  const q = useQuery({
    queryKey: ['busca-global', busca, fontes.map((f) => `${f.slug}.${f.chave}`).join(',')],
    // O `signal` cancela a requisição quando o termo muda (a consulta anterior perde a tela).
    queryFn: ({ signal }) => get<RespostaBuscaGlobal>('/busca-global', { q: busca, fontes: fontes.map((f) => `${f.slug}.${f.chave}`).join(',') }, { signal }),
    enabled: aberta && pronta && fontes.length > 0,
    staleTime: 15_000,
    retry: false,
  });
  // O idioma e o modo discreto entram na montagem a cada render (não na consulta): trocar um deles não busca de novo.
  const montado = pronta && q.data ? gruposDaResposta(fontes, q.data, (slug) => i18n.getFixedT(null, slug), s.workspace?.fuso) : { grupos: [], indisponiveis: 0 };
  const grupos = montado.grupos;
  type Opcao = { tipo: 'item'; item: ItemBuscaGlobal } | { tipo: 'todos'; link: string } | { tipo: 'recente'; termo: string };
  const opcoes: Opcao[] = pronta
    ? grupos.flatMap((g) => [...g.itens.map((item) => ({ tipo: 'item' as const, item })), ...(g.todos ? [{ tipo: 'todos' as const, link: g.todos }] : [])])
    : recentes.map((r) => ({ tipo: 'recente' as const, termo: r }));
  useEffect(() => {
    setAtivo(0);
  }, [busca, aberta]);

  // Clique fora e o foco saindo da caixa (Tab) fecham. Lista de 30/10 B (item 5): fechar limpa o campo, achando ou não (o
  // termo buscado fica nas recentes, para voltar a ele sem redigitar).
  const encerrar = useRef<() => void>(() => undefined);
  encerrar.current = () => {
    // Só o termo que chegou a ser buscado (o parado há 250 ms), não o que ainda estava sendo digitado.
    if (busca.length >= 2 && busca === termo.trim()) {
      guardarRecente(s.usuario.id, busca);
      setRecentes(lerRecentes(s.usuario.id));
    }
    setTermo('');
    setAberta(false);
    setCelular(false);
  };
  useEffect(() => {
    if (!aberta) return;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) encerrar.current();
    };
    const saiu = (e: FocusEvent) => {
      const para = e.relatedTarget as Node | null;
      if (para && caixa.current && !caixa.current.contains(para)) encerrar.current();
    };
    const el = caixa.current;
    document.addEventListener('mousedown', fora);
    el?.addEventListener('focusout', saiu);
    return () => {
      document.removeEventListener('mousedown', fora);
      el?.removeEventListener('focusout', saiu);
    };
  }, [aberta]);

  if (!fontes.length) return null;

  const fechar = () => {
    encerrar.current();
    campo.current?.blur();
  };
  const abrir = (link: string) => {
    guardarRecente(s.usuario.id, termo.trim());
    setRecentes(lerRecentes(s.usuario.id));
    fechar();
    navegar(link);
  };
  const escolher = (i: number) => {
    const o = opcoes[i];
    if (!o) return;
    if (o.tipo === 'item') abrir(o.item.link);
    else if (o.tipo === 'todos') abrir(o.link);
    else {
      setTermo(o.termo);
      campo.current?.focus();
    }
  };
  const teclar = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      fechar();
    } else if (e.key === 'ArrowDown' && opcoes.length) {
      e.preventDefault();
      setAberta(true);
      setAtivo((a) => (a + 1) % opcoes.length);
    } else if (e.key === 'ArrowUp' && opcoes.length) {
      e.preventDefault();
      setAtivo((a) => (a - 1 + opcoes.length) % opcoes.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      escolher(ativo);
    }
  };
  const idOpcao = (i: number) => `${id}-op-${i}`;
  let n = -1;

  return (
    <>
      <button
        type="button"
        className="flex size-11 shrink-0 items-center justify-center rounded-lg text-tinta-2 hover:bg-sup-2 hover:text-tinta-1 md:hidden"
        aria-label={t('casca.busca')}
        title={t('casca.busca')}
        aria-expanded={celular}
        data-abrir-busca=""
        onClick={() => {
          setCelular(true);
          setAberta(true);
          setTimeout(() => campo.current?.focus(), 0);
        }}
      >
        <Search className="size-5" aria-hidden />
      </button>
      <div
        ref={caixa}
        data-busca-global=""
        className={`${celular ? 'absolute inset-x-0 top-full z-40 block border-b border-borda bg-fundo p-2' : 'relative hidden md:block'} w-full md:max-w-52 xl:max-w-85`}
      >
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <input
            ref={campo}
            type="text"
            role="combobox"
            aria-label={t('casca.busca')}
            aria-expanded={aberta}
            aria-controls={`${id}-lista`}
            aria-autocomplete="list"
            aria-activedescendant={aberta && opcoes.length ? idOpcao(ativo) : undefined}
            placeholder={t('casca.buscaPlaceholder')}
            maxLength={100}
            autoComplete="off"
            value={termo}
            onChange={(e) => {
              setTermo(e.target.value);
              setAberta(true);
            }}
            onFocus={() => setAberta(true)}
            onKeyDown={teclar}
            className="h-10 w-full min-w-0 rounded-lg border border-borda-campo bg-campo pr-9 pl-9 md:h-9 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
          {(termo || celular) && (
            <button type="button" aria-label={t('comum.fechar')} onClick={fechar} className="absolute top-1/2 right-1 flex size-8 -translate-y-1/2 items-center justify-center rounded text-tinta-3 hover:text-tinta-1">
              <X className="size-4" aria-hidden />
            </button>
          )}
        </div>
        {aberta && (
          <div id={`${id}-lista`} role="listbox" aria-label={t('casca.busca')} data-resultados-busca="" className="absolute inset-x-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-xl border border-borda bg-sup-1 p-2 shadow-cartao md:inset-x-auto md:w-md">
            {!pronta ? (
              <>
                <p className="kicker px-2 py-1">{t('casca.buscasRecentes')}</p>
                {recentes.length ? (
                  recentes.map((r) => {
                    n += 1;
                    const i = n;
                    return (
                      <div key={r} id={idOpcao(i)} role="option" aria-selected={ativo === i} onMouseDown={(e) => e.preventDefault()} onClick={() => escolher(i)} className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm ${ativo === i ? 'bg-sup-3' : 'hover:bg-sup-2'}`}>
                        <History className="size-4 shrink-0 text-tinta-3" aria-hidden />
                        <TextoCortado className="min-w-0">{r}</TextoCortado>
                      </div>
                    );
                  })
                ) : (
                  <p className="px-2 py-2 text-sm text-tinta-3">{t(termo.trim() ? 'casca.digiteMais' : 'casca.nenhumaRecente')}</p>
                )}
              </>
            ) : q.isPending ? (
              <p className="px-2 py-2 text-sm text-tinta-3" role="status">
                {t('casca.buscando')}
              </p>
            ) : grupos.length === 0 ? (
              <p className="px-2 py-2 text-sm text-tinta-3" role="status" data-busca-vazia="">
                {q.isError ? t('estados.servidorIndisponivel') : t('casca.nenhumResultado', { termo: busca })}
              </p>
            ) : (
              <>
                {grupos.map((g) => {
                  const Icone = g.fonte.icone;
                  const nome = t(`${g.fonte.slug}:${g.fonte.grupo}`);
                  return (
                    <div key={`${g.fonte.slug}.${g.fonte.chave}`} role="group" aria-label={nome} data-grupo-busca={`${g.fonte.slug}.${g.fonte.chave}`} className="mb-1">
                      <p className="kicker flex items-center gap-1.5 px-2 pt-2 pb-1">
                        <Icone className="size-3.5 shrink-0" aria-hidden />
                        {nome}
                      </p>
                      {g.itens.map((item) => {
                        n += 1;
                        const i = n;
                        return (
                          <div key={item.id} id={idOpcao(i)} role="option" aria-selected={ativo === i} onMouseDown={(e) => e.preventDefault()} onClick={() => escolher(i)} className={`flex cursor-pointer flex-col rounded-lg px-2 py-1.5 ${ativo === i ? 'bg-sup-3' : 'hover:bg-sup-2'}`}>
                            <TextoCortado className="text-sm font-medium text-acento-forte">{item.rotulo}</TextoCortado>
                            {item.detalhe && <TextoCortado className="text-xs text-tinta-3">{item.detalhe}</TextoCortado>}
                          </div>
                        );
                      })}
                      {g.todos &&
                        (() => {
                          n += 1;
                          const i = n;
                          return (
                            <div id={idOpcao(i)} role="option" aria-selected={ativo === i} data-ver-todos="" onMouseDown={(e) => e.preventDefault()} onClick={() => escolher(i)} className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-acento-forte ${ativo === i ? 'bg-sup-3' : 'hover:bg-sup-2'}`}>
                              <ArrowRight className="size-3.5 shrink-0" aria-hidden />
                              {t('casca.verTodos', { grupo: nome })}
                            </div>
                          );
                        })()}
                    </div>
                  );
                })}
                {montado.indisponiveis > 0 && (
                  <p className="px-2 pt-1 text-xs text-tinta-3" role="status">
                    {t('casca.buscaIndisponivel')}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
